// src/pages/widgets/SearchWidget.js
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api'; 
import './style/search.css';

const SearchWidget = ({ onClose, onSearch }) => {
  const [query, setQuery] = useState('');
  
  const [filters, setFilters] = useState({
    organisation: 'all',
    category: 'all',
    format: 'all',
    startDate: '',
    endDate: '',
    expireStartDate: '',
    expireEndDate: '',
    showExpiring: false 
  });

  const [options, setOptions] = useState({
    organisations: [],
    categories: [],
    formats: []
  });

  const [isLoading, setIsLoading] = useState(true);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [orgsData, docsData] = await Promise.all([
          apiService.getOrganisations(),
          apiService.getDocuments()
        ]);

        const orgNames = orgsData.map(org => org.name);
        const uniqueCategories = [...new Set(docsData.map(doc => doc.category))].filter(Boolean).sort();
        const uniqueFormats = [...new Set(docsData.map(doc => doc.format))].filter(Boolean).sort();

        setOptions({
          organisations: orgNames,
          categories: uniqueCategories,
          formats: uniqueFormats
        });
      } catch (error) {
        console.error("Failed to load filter options:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFilterOptions();
  }, []);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // --- MODIFIED: Handle Month Selection ---
  const handleMonthSelect = (e) => {
    const months = parseInt(e.target.value, 10);
    
    if (isNaN(months) || months === 0) return; // Do nothing if default selected

    const today = new Date();
    const futureDate = new Date();
    
    // Add months to current date
    futureDate.setMonth(today.getMonth() + months);

    setFilters(prev => ({
      ...prev,
      expireStartDate: formatDate(today),
      expireEndDate: formatDate(futureDate),
      showExpiring: false 
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleExpiringSwitch = (checked) => {
    setFilters(prev => ({
      ...prev,
      showExpiring: checked,
      expireStartDate: checked ? '' : prev.expireStartDate,
      expireEndDate: checked ? '' : prev.expireEndDate
    }));
  };

  const handleOrgSelect = (value) => {
    handleFilterChange('organisation', value);
    setShowOrgDropdown(false);
  };

  const handleApply = () => {
    onSearch({ query, ...filters });
    onClose();
  };

  const handleClear = () => {
    setQuery('');
    setFilters({ 
      organisation: 'all', 
      category: 'all', 
      format: 'all', 
      startDate: '', 
      endDate: '',
      expireStartDate: '',
      expireEndDate: '',
      showExpiring: false
    });
  };

  const filteredOrganisations = options.organisations.filter(org => 
    filters.organisation === 'all' || 
    org.toLowerCase().includes(filters.organisation.toLowerCase())
  );

  const switchStyles = {
    container: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' },
    label: { margin: 0, fontWeight: 500, color: '#333', fontSize: '0.9rem' },
    toggle: { position: 'relative', display: 'inline-block', width: '40px', height: '20px' },
    input: { opacity: 0, width: 0, height: 0 },
    slider: { position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#ccc', transition: '.4s', borderRadius: '34px' },
    sliderChecked: { backgroundColor: '#111827' },
    sliderBefore: { position: 'absolute', content: '""', height: '16px', width: '16px', left: '2px', bottom: '2px', backgroundColor: 'white', transition: '.4s', borderRadius: '50%' },
    sliderBeforeChecked: { transform: 'translateX(20px)' }
  };

  return (
    <div className="dsw-overlay" onClick={onClose}>
      <div className="dsw-container" onClick={(e) => e.stopPropagation()}>
        
        <div className="dsw-header">
          <h2>Filter Documents</h2>
          <button className="dsw-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="dsw-content" style={{ padding: 0 }}>
          
          <div className="dsw-search-bar-container">
            <span className="dsw-search-icon">🔎</span>
            <input 
              type="text" 
              className="dsw-main-search-input"
              placeholder="Search by filename..." 
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApply()}
            />
          </div>

          <div className="dsw-filter-grid">
            
            <div className="dsw-form-group dsw-full-width" style={{ position: 'relative' }}>
              <label className="dsw-input-label">Organisation</label>
              <input 
                type="text" 
                className="dsw-input"
                placeholder="Type to search organisation..."
                value={filters.organisation === 'all' ? '' : filters.organisation}
                onChange={(e) => handleFilterChange('organisation', e.target.value)}
                onFocus={() => setShowOrgDropdown(true)}
                onBlur={() => setTimeout(() => setShowOrgDropdown(false), 200)}
                autoComplete="off"
                disabled={isLoading}
              />
              {showOrgDropdown && (
                <div className="dsw-dropdown-menu">
                  <div 
                    className="dsw-dropdown-item"
                    onMouseDown={() => handleOrgSelect('all')}
                  >
                    All Organisations
                  </div>
                  {filteredOrganisations.length > 0 ? (
                    filteredOrganisations.map((org, index) => (
                      <div 
                        key={index} 
                        className="dsw-dropdown-item"
                        onMouseDown={() => handleOrgSelect(org)}
                      >
                        {org}
                      </div>
                    ))
                  ) : (
                    <div className="dsw-dropdown-item no-result">No matches found</div>
                  )}
                </div>
              )}
            </div>

            <div className="dsw-form-group">
              <label className="dsw-input-label">Category</label>
              <select 
                className="dsw-input"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                disabled={isLoading}
              >
                <option value="all">All Categories</option>
                {options.categories.map((cat, index) => (
                  <option key={index} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="dsw-form-group">
              <label className="dsw-input-label">Format</label>
              <select 
                className="dsw-input"
                value={filters.format}
                onChange={(e) => handleFilterChange('format', e.target.value)}
                disabled={isLoading}
              >
                <option value="all">All Formats</option>
                {options.formats.map((fmt, index) => (
                  <option key={index} value={fmt}>{fmt}</option>
                ))}
              </select>
            </div>

            <div className="dsw-form-group dsw-full-width">
              <label className="dsw-input-label">Uploaded Date Range</label>
              <div className="dsw-date-range-container">
                <input 
                  type="date" 
                  className="dsw-input"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
                <span className="dsw-date-separator">to</span>
                <input 
                  type="date" 
                  className="dsw-input"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </div>
            </div>

            <div className="dsw-form-group dsw-full-width">
              <div style={switchStyles.container}>
                <span style={switchStyles.label}>Show Expired / Expiring in 30 Days</span>
                <label style={switchStyles.toggle}>
                  <input 
                    type="checkbox" 
                    style={switchStyles.input}
                    checked={filters.showExpiring}
                    onChange={(e) => handleExpiringSwitch(e.target.checked)}
                  />
                  <span style={{
                    ...switchStyles.slider,
                    ...(filters.showExpiring ? switchStyles.sliderChecked : {})
                  }}>
                    <span style={{
                      ...switchStyles.sliderBefore,
                      ...(filters.showExpiring ? switchStyles.sliderBeforeChecked : {})
                    }}></span>
                  </span>
                </label>
              </div>
            </div>

            <div className="dsw-form-group dsw-full-width" style={{ opacity: filters.showExpiring ? 0.5 : 1, pointerEvents: filters.showExpiring ? 'none' : 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <label className="dsw-input-label" style={{ marginBottom: 0 }}>Custom Expiration Date Range</label>
                
                {/* --- MODIFIED: Dropdown for Months --- */}
                <select 
                  className="dsw-input" 
                  style={{ width: 'auto', padding: '4px 8px', fontSize: '0.85rem' }}
                  onChange={handleMonthSelect}
                  defaultValue="0"
                >
                  <option value="0">Quick Select...</option>
                  <option value="1">1 Month from now</option>
                  <option value="2">2 Months from now</option>
                  <option value="3">3 Months from now</option>
                  <option value="4">4 Months from now</option>
                  <option value="5">5 Months from now</option>
                </select>
              </div>

              <div className="dsw-date-range-container">
                <input 
                  type="date" 
                  className="dsw-input"
                  value={filters.expireStartDate}
                  onChange={(e) => handleFilterChange('expireStartDate', e.target.value)}
                />
                <span className="dsw-date-separator">to</span>
                <input 
                  type="date" 
                  className="dsw-input"
                  value={filters.expireEndDate}
                  onChange={(e) => handleFilterChange('expireEndDate', e.target.value)}
                />
              </div>
            </div>

          </div>
        </div>

        <div className="dsw-footer">
          <button className="dsw-btn-secondary" onClick={handleClear}>Reset</button>
          <button className="dsw-btn-primary" onClick={handleApply}>Search Documents</button>
        </div>

      </div>
    </div>
  );
};

export default SearchWidget;
